import { BoughtCarDeliveryAddressForm } from '@components/BoughtCarDeliveryAddressTransportation';
import InfoTooltip from '@components/InfoTooltip';
import UnitValue from '@components/UnitValue';
import { CircularProgress, makeStyles, MenuItem, Select } from '@material-ui/core';
import { ReactElement, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BidBuyFormType } from 'src/hooks/useBidBuyForm';
import { PROVIDERS_WITH_FEE, transportOptionLocalStorage } from 'src/modules/global-vars';
import { getLabel, transportOptionProvider } from 'src/modules/labels';

import { SourceType, TransportOptionDTO } from '../../modules/generated/api';
import { TransportationProvider, TransportOptionForPackage } from '../../modules/generated/api/api';

const useStyles = makeStyles((theme) => ({
  loadingIcon: {
    position: 'absolute',
    right: theme.spacing(3),
  },
}));

type TransportSelectBaseProps = {
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  children: ReactNode;
};

const TransportSelectBase = ({
  className,
  disabled,
  loading = false,
  fullWidth = false,
  required,
  children,
}: TransportSelectBaseProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { control } = useFormContext<BidBuyFormType>();
  return (
    <Controller
      name="selectedTransportOption"
      control={control}
      rules={{ required }}
      render={({ field }) => (
        <Select
          {...field}
          disabled={disabled || loading}
          endAdornment={loading ? <CircularProgress className={classes.loadingIcon} size={14} color="secondary" /> : ''}
          variant="standard"
          className={className}
          value={field.value || 'empty'}
          fullWidth={fullWidth}
        >
          <MenuItem value="empty" disabled>
            {t('bids.transportOptionSelect')}
          </MenuItem>
          {children}
        </Select>
      )}
    />
  );
};

type TransportSelectProps = {
  options?: TransportOptionDTO[];
  className?: string;
  disabled?: boolean;
  source?: SourceType;
  dataIsMissing?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
};

export const TransportSelect = ({
  options = [],
  className,
  disabled,
  source,
  dataIsMissing,
  loading = false,
  fullWidth = false,
}: TransportSelectProps): ReactElement | null => {
  const { t } = useTranslation();
  return (
    <TransportSelectBase
      className={className}
      disabled={disabled || dataIsMissing}
      loading={loading && !dataIsMissing}
      fullWidth={fullWidth}
      required={source === SourceType.Vwfs}
    >
      {options
        ?.filter((option) => option.provider !== TransportationProvider.PlatformDefault) // Workaround disable Default for now IRIS-4837
        .map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            onClick={() => option.provider && localStorage.setItem(transportOptionLocalStorage, option.provider)}
          >
            <div style={{ display: 'flex', width: '100%', gap: 5, alignItems: 'center' }}>
              {getLabel(transportOptionProvider, option.provider || '')}
              {option.cost && (
                <span>
                  (
                  <UnitValue
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    value={option.cost.amount}
                    unit={option.cost.currency}
                  />
                  )
                </span>
              )}
              {option.provider && PROVIDERS_WITH_FEE.includes(option.provider) && (
                <InfoTooltip xsIcon title={t('transportation.selfCollectionFee-info')} placement="left" />
              )}
            </div>
          </MenuItem>
        ))}
    </TransportSelectBase>
  );
};

type TransportSelectPackageProps = {
  options?: TransportOptionForPackage[];
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
};

export const TransportSelectPackage = ({
  options = [],
  className,
  disabled,
  loading = false,
  fullWidth = false,
}: TransportSelectPackageProps): ReactElement | null => {
  const { t } = useTranslation();
  return (
    <TransportSelectBase className={className} disabled={disabled} loading={loading} fullWidth={fullWidth} required>
      {options?.map((option) => (
        <MenuItem
          key={option.id}
          value={option.id}
          onClick={() =>
            option.transportationProvider &&
            localStorage.setItem(transportOptionLocalStorage, option.transportationProvider)
          }
        >
          <div style={{ display: 'flex', width: '100%', gap: 5, alignItems: 'center' }}>
            {getLabel(transportOptionProvider, option.transportationProvider || '')}
            {option.costPerCar ? (
              <>
                <span>
                  ({option.numberOfCars} x{' '}
                  <UnitValue
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    value={option.costPerCar.amount}
                    unit={option.costPerCar.currency}
                  />
                  )
                </span>
                {option.totalCost && (
                  <InfoTooltip
                    xsIcon
                    title={t('transportation.packageTransportTotalCost', {
                      amount: option.totalCost.amount,
                      currency: option.totalCost.currency,
                    })}
                    placement="left"
                  />
                )}
              </>
            ) : (
              option.totalCost && (
                <>
                  <span>
                    (
                    <UnitValue
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      value={option.totalCost.amount}
                      unit={option.totalCost.currency}
                    />
                    )
                  </span>
                  <InfoTooltip xsIcon title={t('transportation.packageTransportDifferentPrices')} placement="left" />
                </>
              )
            )}
          </div>
        </MenuItem>
      ))}
    </TransportSelectBase>
  );
};

type TransportSelectBoughtCarProps = {
  options?: TransportOptionDTO[];
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  preSelectedId?: string;
};

export const TransportSelectBoughtCar = ({
  options = [],
  className,
  disabled,
  loading = false,
  fullWidth = false,
  preSelectedId,
}: TransportSelectBoughtCarProps): ReactElement | null => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { control } = useFormContext<BoughtCarDeliveryAddressForm>();
  return (
    <Controller
      name="transportOptionId"
      control={control}
      rules={{ required: true }}
      defaultValue={preSelectedId}
      render={({ field }) => (
        <Select
          {...field}
          disabled={disabled || loading}
          endAdornment={loading ? <CircularProgress className={classes.loadingIcon} size={14} color="secondary" /> : ''}
          variant="standard"
          className={className}
          value={field.value || 'empty'}
          fullWidth={fullWidth}
        >
          <MenuItem value="empty" disabled>
            {t('bids.transportOptionSelect')}
          </MenuItem>
          {options?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <div style={{ display: 'flex', width: '100%', gap: 5, alignItems: 'center' }}>
                {getLabel(transportOptionProvider, option.provider || '')}
                {option.cost && (
                  <span>
                    (
                    <UnitValue
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      value={option.cost.amount}
                      unit={option.cost.currency}
                    />
                    )
                  </span>
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
      )}
    />
  );
};
